/**
 * Read a Persian text field off a database row.
 *
 * ## What this used to be
 *
 * A locale picker. The API returns both halves of every translatable column
 * (`titleFa`/`titleEn`, `nameFa`/`nameEn`, …) and this hook chose between them
 * based on the active language, with Persian as a one-way fallback.
 *
 * English was removed in Phase 18a, so there is nothing left to choose. The
 * helper is kept — rather than rewriting ~90 call sites to `row.titleFa` —
 * for two reasons:
 *
 *  1. It is the single place that knows the `<field>Fa` naming convention. If
 *     the `_en` columns are ever dropped in a migration, or the convention
 *     changes, this file changes and nothing else does.
 *  2. `L(row, 'title')` reads the same whether the row is a product, a
 *     category, a banner or a post. Ninety hand-written `?? ''` fallbacks would
 *     not stay consistent.
 *
 * It is no longer a hook internally — there is no language to subscribe to —
 * but it keeps the `useLocalized()` shape so call sites are untouched.
 */

/** Any object carrying `<field>Fa` keys. */
type Localized = object;

export function pickLocalized(row: Localized | null | undefined, field: string): string {
  if (!row) return '';
  const value = (row as Record<string, unknown>)[`${field}Fa`];
  return typeof value === 'string' ? value : '';
}

/**
 * Component-facing helper.
 *
 * @example
 *   const L = useLocalized();
 *   <h3>{L(product, 'title')}</h3>
 */
export function useLocalized() {
  return (row: Localized | null | undefined, field: string) => pickLocalized(row, field);
}
